import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import RequireFeature from "./components/RequireFeature";
import RequirePermission from "./components/RequirePermission";
import MainLayout from "./layouts/MainLayout";
import AppointmentDetail from "./pages/AppointmentDetail";
import BranchDetail from "./pages/BranchDetail";
import BranchProducts from "./pages/BranchProducts";
import BranchServices from "./pages/BranchServices";
import Brands from "./pages/Brands";
import Branches from "./pages/Branches";
import ClientProfile from "./pages/ClientProfile";
import Clients from "./pages/Clients";
import Billing from "./pages/Billing";
import BusinessProfile from "./pages/BusinessProfile";
import CreateBranch from "./pages/CreateBranch";
import Dashboard from "./pages/Dashboard";
import Diary from "./pages/Diary";
import Login from "./pages/Login";
import LoyaltyMember from "./pages/LoyaltyMember";
import LoyaltyMembers from "./pages/LoyaltyMembers";
import LoyaltyProgram from "./pages/LoyaltyProgram";
import LoyaltyPrograms from "./pages/LoyaltyPrograms";
import LoyaltyRedeem from "./pages/LoyaltyRedeem";
import LoyaltyRewards from "./pages/LoyaltyRewards";
import Pricing from "./pages/Pricing";
import Register from "./pages/Register";
import SignupPlan from "./pages/SignupPlan";
import VerifyEmail from "./pages/VerifyEmail";
import NotificationTarget from "./pages/NotificationTarget";
import NoticeDetail from "./pages/NoticeDetail";
import Notices from "./pages/Notices";
import NoticeWizard from "./pages/NoticeWizard";
import ProductCategories from "./pages/ProductCategories";
import Products from "./pages/Products";
import ProductForm from "./pages/ProductForm";
import Reports from "./pages/Reports";
import Reviews from "./pages/Reviews";
import Alerts from "./pages/Alerts";
import ProviderProfile from "./pages/ProviderProfile";
import Providers from "./pages/Providers";
import Roles from "./pages/Roles";
import ServiceCatalogue from "./pages/ServiceCatalogue";
import ServiceCategories from "./pages/ServiceCategories";
import Staff from "./pages/Staff";
import Transactions from "./pages/Transactions";
import Security from "./pages/Security";
import { adoptUserLanguage } from "./i18n";
import { accountTag, forgetAccountState, takeLastAccount } from "./services/accountState";
import { fetchMe, getCurrentUser, isAuthenticated, logout } from "./services/authService";
import { isGated, TRIAL_EXPIRED_EVENT, TRIAL_EXPIRED_OPEN_PATHS } from "./services/subscriptionService";
import { awaitingPlanStep } from "./services/signupService";

/** Signed out: to login, remembering where they were going. */
function ToLogin() {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
}

/**
 * While gated (trial ended, payment pending with nothing to fall back on, no
 * plan), only plans / billing, the business page and security stay open:
 * every other screen goes to /billing (the API answers those with 402 anyway).
 */
function TrialExpiredGuard({ user, children }) {
  const { pathname } = useLocation();
  const open = TRIAL_EXPIRED_OPEN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  if (isGated(user) && !open) return <Navigate to="/billing" replace />;
  return children;
}

/**
 * Just signed in on /login: continue to where they were going, else home.
 *
 * Only when it is the same account that left (or nobody left this tab — a
 * deep link opened signed out). After a switch, "where they were going" was
 * the previous account's page, often with its branch ids in the query
 * (`/diary?branch=…`, `/?branches=…`): following it would send the new
 * account's first requests with someone else's ids. Home instead.
 */
function AfterLogin() {
  const from = useLocation().state?.from;
  const [target] = useState(() => {
    const left = takeLastAccount();
    const same = !left || left === accountTag(getCurrentUser());
    return same && typeof from === "string" && from.startsWith("/") ? from : "/";
  });
  return <Navigate to={target} replace />;
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [user, setUser] = useState(getCurrentUser());

  // Keep tabs in sync when one of them logs in or out.
  useEffect(() => {
    const sync = () => {
      setAuthenticated(isAuthenticated());
      setUser(getCurrentUser());
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  // The cached profile is what every `can()` in the portal reads, and it was
  // only ever written at login — so a permission granted or withdrawn while
  // somebody was signed in did not reach their screen until they logged out
  // and back in. Refetch it whenever the app boots and whenever the tab is
  // brought back to the front, and render the cached copy meanwhile so nothing
  // flashes. Hiding a screen is only ever a convenience; the server refuses
  // the action either way.
  useEffect(() => {
    if (!authenticated) return undefined;

    const refresh = ({ adoptLanguage = false } = {}) => {
      fetchMe()
        .then((fresh) => {
          setUser(fresh);
          // On load only: a reload keeps the user's own language. Not on every
          // tab focus, which would undo a switch made while it was in flight.
          if (adoptLanguage) adoptUserLanguage(fresh);
        })
        .catch(() => {});
    };

    refresh({ adoptLanguage: true });

    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };

    document.addEventListener("visibilitychange", onVisible);
    // A 402 trial_expired from any request: re-read the status (and so redirect).
    const onTrialExpired = () => refresh();
    window.addEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(TRIAL_EXPIRED_EVENT, onTrialExpired);
    };
  }, [authenticated]);

  /** After a plan change: the fresh profile carries the new status and features. */
  const refreshUser = useCallback(() => fetchMe().then(setUser).catch(() => {}), []);

  const handleLogin = useCallback((loggedInUser) => {
    // A session may have ended in another tab without this one cleaning up:
    // start the new account from nothing (see accountState).
    forgetAccountState();
    setUser(loggedInUser);
    setAuthenticated(true);
  }, []);

  const handleLogout = useCallback(async () => {
    // Takes this browser's push token with it (see authService.logout).
    await logout();
    setUser(null);
    setAuthenticated(false);
  }, []);

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        {/* Public: the pricing page and self sign-up. Signed in, both fall
            through to the dashboard. */}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/register" element={<Register />} />
        {/* Sign-up step 2: the emailed code (also reached from login while unverified). */}
        <Route path="/verify-email" element={<VerifyEmail onLogin={handleLogin} />} />
        <Route path="*" element={<ToLogin />} />
      </Routes>
    );
  }

  // Sign-up step 3: verified, plan not confirmed yet — only the plan step is open
  // (the API answers everything else with 403 signup_plan_required).
  if (awaitingPlanStep(user)) {
    return (
      <Routes>
        <Route path="/signup/plan" element={<SignupPlan onDone={(fresh) => (fresh ? handleLogin(fresh) : refreshUser())} onLogout={handleLogout} />} />
        <Route path="*" element={<Navigate to="/signup/plan" replace />} />
      </Routes>
    );
  }

  return (
    <MainLayout user={user} onLogout={handleLogout}>
      <TrialExpiredGuard user={user}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        {/* Plans & billing: current plan, free trial, change plan (owner). */}
        <Route path="/billing" element={<Billing user={user} onChanged={refreshUser} />} />
        <Route path="/business" element={<BusinessProfile />} />
        {/* The diary and one booking's detail — BIZ-12 and BIZ-14. */}
        <Route
          path="/diary"
          element={<RequirePermission path="/diary"><Diary /></RequirePermission>}
        />
        <Route
          path="/appointments/:id"
          element={<RequirePermission path="/appointments/:id"><AppointmentDetail /></RequirePermission>}
        />
        <Route
          path="/branches"
          element={<RequirePermission path="/branches"><Branches /></RequirePermission>}
        />
        <Route
          path="/branches/create"
          element={<RequirePermission path="/branches/create"><CreateBranch /></RequirePermission>}
        />
        <Route
          path="/branches/:id"
          element={<RequirePermission path="/branches/:id"><BranchDetail /></RequirePermission>}
        />
        <Route
          path="/branches/:id/services"
          element={<RequirePermission path="/branches/:id/services"><BranchServices /></RequirePermission>}
        />
        <Route
          path="/branches/:id/products"
          element={<RequirePermission path="/branches/:id/products"><BranchProducts /></RequirePermission>}
        />
        {/* The business's own CRM — BIZ-15 and BIZ-16. */}
        {/* Reports v2 (spec 02) — /reports/:category/:reportKey; old ?tab= URLs redirect inside. */}
        {["/reports", "/reports/:category", "/reports/:category/:reportKey"].map((path) => (
          <Route
            key={path}
            path={path}
            element={<RequirePermission path="/reports"><Reports /></RequirePermission>}
          />
        ))}
        {/* Loyalty — permission per screen, and the plan's loyalty feature. */}
        {[
          ["/loyalty/programs", <LoyaltyPrograms />],
          ["/loyalty/programs/new", <LoyaltyProgram mode="new" />],
          ["/loyalty/programs/:programId", <LoyaltyProgram mode="view" />],
          ["/loyalty/programs/:programId/edit", <LoyaltyProgram mode="edit" />],
          ["/loyalty/rewards", <LoyaltyRewards />],
          ["/loyalty/members", <LoyaltyMembers />],
          ["/loyalty/members/:clientId", <LoyaltyMember />],
          ["/loyalty/redeem", <LoyaltyRedeem />],
        ].map(([path, page]) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission path={path}>
                <RequireFeature path={path}>{page}</RequireFeature>
              </RequirePermission>
            }
          />
        ))}
        {/* The single business-wide programme became one per branch. */}
        <Route path="/loyalty/program" element={<Navigate to="/loyalty/programs" replace />} />
        {/* Customer reviews per branch, read-only. */}
        <Route
          path="/reviews"
          element={<RequirePermission path="/reviews"><Reviews /></RequirePermission>}
        />
        <Route
          path="/alerts"
          element={<RequirePermission path="/alerts"><Alerts /></RequirePermission>}
        />
        {/* Disruption notices: "Notify affected customers". */}
        <Route
          path="/notices"
          element={<RequirePermission path="/notices"><Notices /></RequirePermission>}
        />
        <Route
          path="/notices/new"
          element={<RequirePermission path="/notices/new"><NoticeWizard /></RequirePermission>}
        />
        <Route
          path="/notices/:id"
          element={<RequirePermission path="/notices/:id"><NoticeDetail /></RequirePermission>}
        />
        {/* BIZ-19 — every transaction, with the money broken out. */}
        <Route
          path="/transactions"
          element={<RequirePermission path="/transactions"><Transactions /></RequirePermission>}
        />
        <Route
          path="/clients"
          element={<RequirePermission path="/clients"><Clients /></RequirePermission>}
        />
        <Route
          path="/clients/:id"
          element={<RequirePermission path="/clients/:id"><ClientProfile /></RequirePermission>}
        />
        <Route
          path="/services"
          element={<RequirePermission path="/services"><ServiceCatalogue /></RequirePermission>}
        />
        <Route
          path="/service-categories"
          element={<RequirePermission path="/service-categories"><ServiceCategories /></RequirePermission>}
        />
        <Route
          path="/product-categories"
          element={<RequirePermission path="/product-categories"><ProductCategories /></RequirePermission>}
        />
        {/* The product catalogue, like /services: defined once, assigned to branches explicitly. */}
        <Route
          path="/products"
          element={<RequirePermission path="/products"><Products /></RequirePermission>}
        />
        <Route
          path="/products/create"
          element={<RequirePermission path="/products/create"><ProductForm /></RequirePermission>}
        />
        <Route
          path="/products/:productId/edit"
          element={<RequirePermission path="/products/:productId/edit"><ProductForm /></RequirePermission>}
        />
        <Route
          path="/brands"
          element={<RequirePermission path="/brands"><Brands /></RequirePermission>}
        />
        <Route
          path="/staff"
          element={<RequirePermission path="/staff"><Staff /></RequirePermission>}
        />
        {/* Service providers — the people customers book, as opposed to the
            portal logins on /staff. */}
        <Route
          path="/providers"
          element={<RequirePermission path="/providers"><Providers /></RequirePermission>}
        />
        <Route
          path="/providers/:id"
          element={<RequirePermission path="/providers/:id"><ProviderProfile /></RequirePermission>}
        />
        <Route
          path="/roles"
          element={<RequirePermission path="/roles"><Roles /></RequirePermission>}
        />
        <Route path="/security" element={<Security user={user} />} />
        <Route path="/notification-target" element={<NotificationTarget />} />
        <Route path="/login" element={<AfterLogin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </TrialExpiredGuard>
    </MainLayout>
  );
}
